import { useCallback, useMemo } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import type { Campaign } from '@/services/db/campaign/entity';
import { useCampaignsForWorkspace } from '@/services/db/campaign/queries';
import { isBuilderPlan } from '@/services/auth/plan-slugs';
import { generatePath } from '@/services/router/generate-path';
import type { Route } from '@/services/router';
import type { LocalCommand } from './local-commands';
import type { PermissionGuard } from './use-navigation-search';
import { matchesLocalCommand } from './utils/search-commands';
import {
  addRoutingCommand,
  getNumericRouteParamFromPathname,
  matchesCurrentWorkspaceRoute,
  type Navigate,
} from './routing-command-utils';

type BuildCampaignRoutingCommandsArgs = {
  query: string;
  workspaceId: number;
  pathname: string;
  campaignState?: Campaign['state'] | null;
  guard: PermissionGuard | null;
  navigate: Navigate;
};

type UseCampaignRoutingCommandsDeps = {
  workspaceId: number;
  guard: PermissionGuard | null;
  navigate: Navigate;
};

const CAMPAIGN_DETAIL_ROUTE = '/workspaces/:workspaceId/journeys/campaigns/:campaignId' satisfies Route;
const CAMPAIGN_OVERVIEW_ROUTE = '/workspaces/:workspaceId/journeys/campaigns/:campaignId/overview/*';
const CAMPAIGN_SETUP_ROUTE = '/workspaces/:workspaceId/journeys/campaigns/:campaignId/setup/*';
const CAMPAIGN_STATIC_ROUTES = [
  '/workspaces/:workspaceId/journeys/campaigns',
  '/workspaces/:workspaceId/journeys/campaigns/new',
  '/workspaces/:workspaceId/journeys/campaigns/welcome',
  '/workspaces/:workspaceId/journeys/campaigns/welcome/create',
] as const satisfies readonly Route[];
const MAX_TYPED_CAMPAIGN_ROUTING_COMMANDS = 4;

type CampaignShortcutSurface = 'setup' | 'overview' | 'unknown';

function matchesCampaignChildRoute({
  pathname,
  pattern,
  workspaceId,
  campaignId,
}: {
  pathname: string;
  pattern: string;
  workspaceId: number;
  campaignId: string;
}): boolean {
  const params = matchPath<'workspaceId' | 'campaignId', string>({ path: pattern, end: false }, pathname)?.params;
  const isCurrentWorkspace = params?.workspaceId === 'last' || params?.workspaceId === String(workspaceId);
  return isCurrentWorkspace && params?.campaignId === campaignId;
}

function getCampaignShortcutSurface({
  pathname,
  workspaceId,
  campaignId,
  campaignState,
}: {
  pathname: string;
  workspaceId: number;
  campaignId: string | null;
  campaignState?: Campaign['state'] | null;
}): CampaignShortcutSurface {
  if (campaignId == null) return 'unknown';

  if (matchesCampaignChildRoute({ pathname, pattern: CAMPAIGN_SETUP_ROUTE, workspaceId, campaignId })) {
    return 'setup';
  }

  if (matchesCampaignChildRoute({ pathname, pattern: CAMPAIGN_OVERVIEW_ROUTE, workspaceId, campaignId })) {
    return 'overview';
  }

  if (campaignState === 'draft') return 'setup';
  if (campaignState != null) return 'overview';

  return 'unknown';
}

function useCachedCampaignState(workspaceId: number, campaignId: string | null): Campaign['state'] | null {
  const selectCampaignState = useCallback(
    (campaigns: readonly Campaign[]) =>
      campaignId == null ? null : (campaigns.find((campaign) => campaign.id === campaignId)?.state ?? null),
    [campaignId],
  );

  return useCampaignsForWorkspace<Campaign['state'] | null>(workspaceId, { select: selectCampaignState });
}

export function getCampaignIdFromPathname(pathname: string, workspaceId: number): string | null {
  return getNumericRouteParamFromPathname({
    pathname,
    workspaceId,
    route: CAMPAIGN_DETAIL_ROUTE,
    paramName: 'campaignId',
  });
}

export function isCampaignPathname(pathname: string, workspaceId: number): boolean {
  if (workspaceId === 0) return false;
  if (getCampaignIdFromPathname(pathname, workspaceId) != null) return true;
  return CAMPAIGN_STATIC_ROUTES.some((route) => matchesCurrentWorkspaceRoute(pathname, route, workspaceId));
}

type CampaignRouteParams = { workspaceId: number; campaignId: string };

type CampaignOverviewCommandDef = {
  buildPath: (params: CampaignRouteParams) => string;
  idSuffix: string;
  label: string;
  description: string;
  keywords: string[];
};

const CAMPAIGN_OVERVIEW_COMMAND_DEFS: readonly CampaignOverviewCommandDef[] = [
  {
    buildPath: (p) => generatePath('/workspaces/:workspaceId/journeys/campaigns/:campaignId/overview', p),
    idSuffix: 'overview',
    label: 'Open campaign overview',
    description: 'Open the campaign overview',
    keywords: ['campaign', 'overview', 'details'],
  },
  {
    buildPath: (p) =>
      generatePath('/workspaces/:workspaceId/journeys/campaigns/:campaignId/overview/workflow/actions', p),
    idSuffix: 'campaign-workflow',
    label: 'View campaign workflow',
    description: 'View the workflow in read-only mode',
    keywords: ['campaign', 'workflow', 'actions'],
  },
  {
    buildPath: (p) => generatePath('/workspaces/:workspaceId/journeys/campaigns/:campaignId/overview/metrics', p),
    idSuffix: 'metrics',
    label: 'View campaign metrics',
    description: 'Review campaign performance',
    keywords: ['campaign', 'metrics', 'analytics', 'report'],
  },
  {
    buildPath: (p) =>
      generatePath('/workspaces/:workspaceId/journeys/campaigns/:campaignId/overview/journey_metrics', p),
    idSuffix: 'journey-metrics',
    label: 'View journey metrics',
    description: 'Review journey-level performance',
    keywords: ['campaign', 'journey metrics', 'analytics', 'report'],
  },
  {
    buildPath: (p) => generatePath('/workspaces/:workspaceId/journeys/campaigns/:campaignId/overview/sent', p),
    idSuffix: 'sent',
    label: 'View sent deliveries',
    description: 'See messages sent by this campaign',
    keywords: ['campaign', 'sent', 'deliveries', 'messages'],
  },
  {
    buildPath: (p) => generatePath('/workspaces/:workspaceId/journeys/campaigns/:campaignId/overview/drafts', p),
    idSuffix: 'drafts',
    label: 'View drafts',
    description: 'Review draft deliveries',
    keywords: ['campaign', 'drafts', 'draft deliveries'],
  },
  {
    buildPath: (p) => generatePath('/workspaces/:workspaceId/journeys/campaigns/:campaignId/overview/tests', p),
    idSuffix: 'tests',
    label: 'View tests',
    description: 'Review test deliveries',
    keywords: ['campaign', 'tests', 'test deliveries'],
  },
  {
    buildPath: (p) => generatePath('/workspaces/:workspaceId/journeys/campaigns/:campaignId/overview/journeys', p),
    idSuffix: 'journeys',
    label: 'View journeys',
    description: 'See people currently in this campaign',
    keywords: ['campaign', 'journeys', 'people'],
  },
  {
    buildPath: (p) =>
      generatePath('/workspaces/:workspaceId/journeys/campaigns/:campaignId/overview/form-submissions', p),
    idSuffix: 'form-submissions',
    label: 'View form submissions',
    description: 'Review form submissions for this campaign',
    keywords: ['campaign', 'form submissions', 'forms'],
  },
];

type AddCampaignDetailCommandsArgs = {
  commands: LocalCommand[];
  pathname: string;
  workspaceId: number;
  campaignId: string;
  shortcutSurface: CampaignShortcutSurface;
  canEditCampaigns: boolean;
  navigate: Navigate;
};

function addCampaignDetailCommands({
  commands,
  pathname,
  workspaceId,
  campaignId,
  shortcutSurface,
  canEditCampaigns,
  navigate,
}: AddCampaignDetailCommandsArgs): void {
  const campaignRouteParams = { workspaceId, campaignId };

  if (canEditCampaigns && shortcutSurface !== 'overview') {
    addRoutingCommand(
      commands,
      pathname,
      navigate,
      generatePath(
        '/workspaces/:workspaceId/journeys/campaigns/:campaignId/setup/workflow/actions',
        campaignRouteParams,
      ),
      {
        id: `campaign-routing:${campaignId}:workflow-builder`,
        label: 'Open workflow builder',
        description: 'Edit the workflow for this draft campaign',
        icon: 'campaigns',
        keywords: ['campaign', 'workflow', 'builder', 'edit', 'actions'],
      },
    );
  }

  if (shortcutSurface !== 'setup') {
    for (const { buildPath, idSuffix, label, description, keywords } of CAMPAIGN_OVERVIEW_COMMAND_DEFS) {
      addRoutingCommand(commands, pathname, navigate, buildPath(campaignRouteParams), {
        id: `campaign-routing:${campaignId}:${idSuffix}`,
        label,
        description,
        icon: 'campaigns',
        keywords,
      });
    }
  }
}

export function buildCampaignRoutingCommands({
  query,
  workspaceId,
  pathname,
  campaignState,
  guard,
  navigate,
}: BuildCampaignRoutingCommandsArgs): LocalCommand[] {
  if (workspaceId === 0) return [];
  if (isBuilderPlan(guard?.account)) return [];
  if (!isCampaignPathname(pathname, workspaceId)) return [];

  const trimmedQuery = query.trim();
  const isEmptyQuery = trimmedQuery.length === 0;

  const campaignId = getCampaignIdFromPathname(pathname, workspaceId);
  const canViewCampaigns = guard?.hasPermission('journeys/campaigns/view') === true;
  const canCreateCampaigns = guard?.hasPermission('journeys/campaigns/create') === true;
  const canEditCampaigns = guard?.hasPermission('journeys/campaigns/edit') === true;
  const commands: LocalCommand[] = [];
  const shortcutSurface = getCampaignShortcutSurface({ pathname, workspaceId, campaignId, campaignState });

  if (campaignId != null && canViewCampaigns) {
    addCampaignDetailCommands({
      commands,
      pathname,
      workspaceId,
      campaignId,
      shortcutSurface,
      canEditCampaigns,
      navigate,
    });
  }

  if (canCreateCampaigns && shortcutSurface !== 'setup') {
    addRoutingCommand(
      commands,
      pathname,
      navigate,
      generatePath('/workspaces/:workspaceId/journeys/campaigns/new', { workspaceId }),
      {
        id: 'campaign-routing:create',
        label: 'Create campaign',
        description: 'Start a new behavioral or date-triggered campaign',
        icon: 'campaigns',
        keywords: ['campaign', 'campaigns', 'create', 'new'],
      },
    );
  }

  if (canViewCampaigns) {
    addRoutingCommand(
      commands,
      pathname,
      navigate,
      generatePath('/workspaces/:workspaceId/journeys/campaigns', { workspaceId }),
      {
        id: 'campaign-routing:index',
        label: 'Go to campaigns',
        description: 'Browse all campaigns',
        icon: 'campaigns',
        keywords: ['campaign', 'campaigns', 'list'],
      },
    );
  }

  const matchingCommands = commands.filter(matchesLocalCommand(query));

  if (isEmptyQuery) return matchingCommands;

  return matchingCommands.slice(0, MAX_TYPED_CAMPAIGN_ROUTING_COMMANDS);
}

export function useCampaignRoutingCommands(query: string, deps: UseCampaignRoutingCommandsDeps): LocalCommand[] {
  const { pathname } = useLocation();
  const { workspaceId, guard, navigate } = deps;
  const campaignId = getCampaignIdFromPathname(pathname, workspaceId);
  const campaignState = useCachedCampaignState(workspaceId, campaignId);

  return useMemo(
    () =>
      buildCampaignRoutingCommands({
        query,
        workspaceId,
        pathname,
        campaignState,
        guard,
        navigate,
      }),
    [query, workspaceId, pathname, campaignState, guard, navigate],
  );
}
